import {
  Box,
  Center,
  Flex,
  Heading,
  Icon,
  Image,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';
import { GiCampfire, GiCampingTent } from 'react-icons/gi';
import { TbArrowsExchange2 } from 'react-icons/tb';
import {
  TYPE_ENABLED,
  DISCOUNT_CODE,
  canSelectEquipment,
  FLEXI_MAX_PAYG,
  FLEXI_MAX_SUB,
  getMaxFlexiDays,
} from '#constants';

import { CalendarIcon } from '@chakra-ui/icons';
import { FaWheelchair, FaMoon } from 'react-icons/fa';
import { MdPeople } from 'react-icons/md';
import TrackerNotification from './TrackerNotification.jsx';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';
import { useEffect } from 'react';

function SelectedCampsites() {
  const {
    outing,
    trackerInput: {
      filters: { campsites },
    },
    campsites: allCampsites,
  } = useUIStore(
    (state) => ({
      outing: state.outing,
      trackerInput: state.trackerInput,
      campsites: state.campsites,
    }),
    shallow
  );
  const canSelectChild =
    TYPE_ENABLED.specific.indexOf(outing?.entity_type) > -1;

  if (!canSelectChild) {
    return <Flex></Flex>;
  }

  const campIdToNameMap = {};
  allCampsites?.forEach((camp) => {
    const id = camp._id?.toString?.() ?? camp._id;
    campIdToNameMap[id] = camp;
    if (camp.campsite_id) campIdToNameMap[camp.campsite_id] = camp;
  });
  if (canSelectChild && !campsites?.length) {
    return null;
  }
  return (
    <Flex align="center" gap={2} flexWrap="wrap">
      <Icon as={GiCampfire} color="brand.500" boxSize={5} flexShrink={0} />
      <Flex gap={2} flexWrap="wrap">
        {campsites.map((camp_id) => {
          const idStr = camp_id?.toString?.() ?? camp_id;
          const name = campIdToNameMap[idStr]?.name ?? 'Selected';
          return (
            <Tag
              key={camp_id}
              colorScheme="brand"
              size="sm"
              borderRadius="md"
              px={3}
              py={1}
            >
              {name}
            </Tag>
          );
        })}
      </Flex>
    </Flex>
  );
}

function PaygFlexiNote({ canSelectFlexiDates }) {
  const { sub, planSlug, setPlanSlug, flexiDaysBefore, flexiDaysAfter } =
    useUIStore(
      (state) => ({
        sub: state.sub,
        planSlug: state.planSlug,
        setPlanSlug: state.setPlanSlug,
        flexiDaysBefore: state.trackerInput.flexiDaysBefore,
        flexiDaysAfter: state.trackerInput.flexiDaysAfter,
      }),
      shallow
    );

  const isPayg = !sub?._id && planSlug === 'pay-as-you-go';
  const maxFlexi = getMaxFlexiDays({ sub, planSlug });

  useEffect(() => {
    const current = Math.max(flexiDaysBefore ?? 0, flexiDaysAfter ?? 0);
    if (current <= maxFlexi) return;
    // Re-apply the selected plan so PAYG remembers the wider window.
    // Monthly/yearly goes through flexiForPlanChange, which caps at ±15
    // so this cannot loop.
    if (planSlug) setPlanSlug(planSlug);
  }, [maxFlexi, flexiDaysBefore, flexiDaysAfter, planSlug, setPlanSlug]);

  if (!canSelectFlexiDates || !isPayg) return null;

  return (
    <Text fontSize="sm" color="gray.600" mt={1}>
      Pay as you go includes up to ±{FLEXI_MAX_PAYG} flexible days. Subscribe for
      up to ±{FLEXI_MAX_SUB}.
    </Text>
  );
}

function TripSummary() {
  const { outing, trackerInput } = useUIStore(
    (state) => ({ outing: state.outing, trackerInput: state.trackerInput }),
    shallow
  );
  const { nights = [], groupSize, flexiDaysBefore } = trackerInput;
  const childNameById = {};
  (outing?.children ?? []).forEach((c) => {
    const id = c._id?.toString?.() ?? String(c._id ?? '');
    if (id) childNameById[id] = c.name;
  });
  const optionName = (opt) => {
    if (opt?.name) return opt.name;
    if (opt?.childId?.name) return opt.childId.name;
    const id =
      opt?.childId?._id?.toString?.() ??
      opt?.childId?.toString?.() ??
      String(opt?.childId ?? '');
    return childNameById[id] || 'Place';
  };
  return (
    <Box
      p={5}
      borderRadius="xl"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderLeft="4px solid"
      borderLeftColor="brand.500"
      boxShadow="sm"
    >
      <Stack spacing={4}>
        <Text fontSize={['lg', 'xl']} fontWeight="bold" color="gray.800">
          {outing.name} · Trip
        </Text>
        <Stack spacing={2}>
          {nights.map((night, idx) => (
            <Flex key={idx} align="center" gap={2} flexWrap="wrap">
              <CalendarIcon color="brand.600" boxSize={4} />
              <Text
                fontSize={['sm', 'md']}
                color="gray.700"
                fontWeight="500"
                minW="max-content"
              >
                Night {idx + 1}: {night.dateString}
              </Text>
              <Flex gap={1} flexWrap="wrap">
                {(night.options ?? []).map((opt, i) => (
                  <Tag
                    key={i}
                    colorScheme="brand"
                    size="sm"
                    borderRadius="md"
                  >
                    {optionName(opt)}
                    {(opt.acceptedChildren?.length ?? 0) > 0
                      ? ` · ${opt.acceptedChildren
                          .map((c) => c.name || c.childId?.name)
                          .filter(Boolean)
                          .join(', ')}`
                      : ''}
                  </Tag>
                ))}
              </Flex>
            </Flex>
          ))}
        </Stack>
        <Flex gap={4} flexWrap="wrap" align="center">
          <Flex align="center" gap={2}>
            <Icon as={MdPeople} color="brand.500" boxSize={5} />
            <Text fontSize="sm" color="gray.600">
              {groupSize} adult{groupSize > 1 ? 's' : ''}
            </Text>
          </Flex>
          {(flexiDaysBefore ?? 0) > 0 && (
            <Flex align="center" gap={1} color="brand.600">
              <Icon as={TbArrowsExchange2} boxSize={4} />
              <Text fontSize="sm" fontWeight="500">
                ±{flexiDaysBefore} day{flexiDaysBefore !== 1 ? 's' : ''}
              </Text>
            </Flex>
          )}
        </Flex>
        <PaygFlexiNote canSelectFlexiDates />
      </Stack>
    </Box>
  );
}

function CampsiteSummary() {
  const {
    outing,
    trackerInput,
    trackerInput: {
      groupSize,
      equipmentType,
      startDate: checkin,
      endDate: checkout,
      filters: { campsites, accessible },
      flexiDaysBefore,
      flexiDaysAfter,
    },
  } = useUIStore(
    (state) => ({
      outing: state.outing,
      trackerInput: state.trackerInput,
    }),
    shallow
  );
  if (trackerInput.type === 'trip') {
    return <TripSummary />;
  }
  const twoDates = TYPE_ENABLED.twoDates.indexOf(outing?.entity_type) > -1;
  const canSelectGroup =
    TYPE_ENABLED.groupSize.indexOf(outing?.entity_type) > -1;
  const canSelectAccessible =
    TYPE_ENABLED.accessible.indexOf(outing?.entity_type) > -1;
  const canSelectFlexiDates =
    TYPE_ENABLED.flexiDates.indexOf(outing?.entity_type) > -1;

  // Calculate number of nights
  const numberOfNights = twoDates && checkin && checkout
    ? Math.round((new Date(checkout) - new Date(checkin)) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Box
      p={5}
      borderRadius="xl"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderLeft="4px solid"
      borderLeftColor="brand.500"
      boxShadow="sm"
      _hover={{ boxShadow: 'md' }}
      transition="box-shadow 0.2s"
    >
      <Stack spacing={4}>
        {/* Outing name */}
        <Text
          fontSize={['lg', 'xl']}
          fontWeight="bold"
          color="gray.800"
          lineHeight="tight"
          letterSpacing="tight"
        >
          {outing.name}
        </Text>

        {/* Date row */}
        <Flex align="center" gap={2} flexShrink={0}>
          <CalendarIcon color="brand.600" boxSize={5} />
          <Text
            fontSize={['sm', 'md']}
            color="gray.700"
            fontWeight="500"
            data-testid="tracker-summary-dates"
          >
            {checkin?.toDateString()}
            {twoDates && (
              <>
                <Text as="span" color="gray.400" mx={1}>
                  →
                </Text>
                {checkout?.toDateString()}
              </>
            )}
          </Text>
        </Flex>
        {(numberOfNights > 0 || (canSelectFlexiDates && (flexiDaysBefore ?? flexiDaysAfter ?? 0) > 0)) && (
          <Flex align="center" gap={4} flexWrap="wrap">
            {numberOfNights > 0 && (
              <Flex align="center" gap={1} color="brand.600">
                <Icon as={FaMoon} boxSize={4} />
                <Text fontSize={['sm', 'md']} fontWeight="500">
                  {numberOfNights} Night{numberOfNights > 1 ? 's' : ''}
                </Text>
              </Flex>
            )}
            {canSelectFlexiDates && (flexiDaysBefore ?? flexiDaysAfter ?? 0) > 0 && (
              <Flex align="center" gap={1} color="brand.600">
                <Icon as={TbArrowsExchange2} boxSize={4} />
                <Text fontSize={['sm', 'md']} fontWeight="500">
                  ±{flexiDaysBefore ?? flexiDaysAfter ?? 0} day{(flexiDaysBefore ?? flexiDaysAfter) !== 1 ? 's' : ''}
                </Text>
              </Flex>
            )}
          </Flex>
        )}
        <PaygFlexiNote canSelectFlexiDates={canSelectFlexiDates} />

        {/* Campsites */}
        <SelectedCampsites />

        {/* Details row */}
        <Flex gap={4} flexWrap="wrap" align="center">
          {canSelectGroup && (
            <Flex align="center" gap={2}>
              <Icon as={MdPeople} color="brand.500" boxSize={5} />
              <Text fontSize="sm" color="gray.600">
                {groupSize} adult{groupSize > 1 ? 's' : ''}
              </Text>
            </Flex>
          )}
          {canSelectEquipment(outing) && (
            <Flex align="center" gap={2}>
              <Icon as={GiCampingTent} color="brand.500" boxSize={5} />
              <Text fontSize="sm" color="gray.600">
                {equipmentType}
              </Text>
            </Flex>
          )}
          {canSelectAccessible && (
            <Flex align="center" gap={2}>
              <Icon as={FaWheelchair} color="brand.500" boxSize={5} />
              <Text fontSize="sm" color="gray.600">
                {accessible ? 'Accessible' : 'Standard'}
              </Text>
            </Flex>
          )}
        </Flex>
      </Stack>
    </Box>
  );
}

export default function TrackerSummary({ showNotificationPrefs = true }) {
  const { code } = useUIStore((state) => ({ code: state.code }), shallow);
  if (code === 'YEAROFF') {
    return (
      <Flex w="100%" direction="column" mt={[0, 4]}>
        <Image
          // fallbackSrc="/public/camp.jpg"
          objectFit={'contain'}
          src={'/public/gift.jpeg'}
          m={2}
          my={[0, 6]}
          alignSelf={'stretch'}
          maxH="200px"
        />
        <Center>
          <Heading as={'h2'} fontSize="3xl" mt={[4, 10]}>
            {DISCOUNT_CODE[code].reviewButtonText}
          </Heading>
          <Text></Text>
        </Center>
      </Flex>
    );
  }
  return (
    <Flex direction="column" gap={6} pb={[0, 4]}>
      <Box>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          letterSpacing="wider"
          color="gray.500"
          textTransform="uppercase"
          mb={2}
        >
          Tracker Summary
        </Text>
        <CampsiteSummary />
      </Box>
      {showNotificationPrefs && <TrackerNotification />}
    </Flex>
  );
}
